import React, { useEffect, useState, useCallback } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    Button, Input, FormControl, FormLabel, HStack, VStack, Text, Badge, Spinner, Flex, Alert, AlertIcon,
    Table, Thead, Tbody, Tr, Th, Td, Link, useToast,
} from '@chakra-ui/react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';

export const previousMonth = () => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Preview a tenant's monthly platform invoice, then send it through Stripe.
 * Nothing is sent until "Send invoice" is clicked and confirmed.
 */
export default function PlatformInvoiceModal({ laundry, isOpen, onClose, platformKey, onSent }) {
    const toast = useToast();
    const [month, setMonth] = useState(previousMonth());
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [email, setEmail] = useState('');
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        if (!laundry) return;
        setLoading(true);
        setError('');
        setPreview(null);
        try {
            const res = await axios.get(`${API_URL}/api/platform/invoices/preview`, {
                params: { laundryId: laundry.laundryId, month },
                headers: { 'x-platform-key': platformKey },
            });
            const p = res.data.preview;
            setPreview(p);
            setEmail(p.existingInvoice?.recipientEmail || p.recipientEmail || '');
        } catch (err) {
            setError(err.response?.data?.detail || 'Could not load the invoice preview.');
        } finally {
            setLoading(false);
        }
    }, [laundry, month, platformKey]);

    useEffect(() => {
        if (isOpen) load();
    }, [isOpen, load]);

    useEffect(() => {
        if (isOpen) setMonth(previousMonth());
    }, [isOpen, laundry]);

    const alreadySent = ['sent', 'sending'].includes(preview?.existingInvoice?.status);
    const canSend = preview && preview.monthFinished && !alreadySent && !preview.isBillingLaundry
        && preview.stripeMode !== 'missing' && email.trim();

    const send = async () => {
        const live = preview.stripeMode === 'live';
        const ok = window.confirm(
            `Send ${preview.laundryName} a ${money(preview.total)} invoice for ${preview.monthLabel} to ${email.trim()}?`
            + (live ? '\n\nThis uses the LIVE Stripe account and emails the client right away.' : '')
        );
        if (!ok) return;
        setSending(true);
        try {
            const res = await axios.post(`${API_URL}/api/platform/invoices/send`, {
                laundryId: laundry.laundryId,
                month,
                recipientEmail: email.trim(),
                expectedTotalCents: preview.totalCents,
            }, { headers: { 'x-platform-key': platformKey } });
            const inv = res.data.invoice;
            toast({
                title: 'Invoice sent',
                description: inv.emailedBy === 'none'
                    ? 'Created in Stripe, but the $0 notice email could not be sent.'
                    : `${money(inv.total)} to ${inv.recipientEmail}`,
                status: inv.emailedBy === 'none' ? 'warning' : 'success',
                duration: 6000,
                isClosable: true,
            });
            onSent && onSent(inv);
            load();
        } catch (err) {
            toast({ title: 'Invoice not sent', description: err.response?.data?.detail || 'Unknown error', status: 'error', duration: 8000, isClosable: true });
            load();
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Invoice — {laundry?.laundryName}
                    {preview && (
                        <Badge ml={2} colorScheme={preview.stripeMode === 'live' ? 'red' : preview.stripeMode === 'test' ? 'yellow' : 'gray'}>
                            Stripe {preview.stripeMode}
                        </Badge>
                    )}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <HStack spacing={4} align="flex-end">
                            <FormControl maxW="200px">
                                <FormLabel fontSize="sm">Billing month</FormLabel>
                                <Input type="month" size="sm" value={month} onChange={(e) => setMonth(e.target.value)} />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Send to</FormLabel>
                                <Input size="sm" type="email" value={email} onChange={(e) => setEmail(e.target.value)} isDisabled={alreadySent} />
                            </FormControl>
                        </HStack>

                        {loading && <Flex justify="center" py={8}><Spinner /></Flex>}
                        {error && <Alert status="error"><AlertIcon />{error}</Alert>}

                        {preview && !loading && (
                            <>
                                {preview.isBillingLaundry && (
                                    <Alert status="info"><AlertIcon />This laundry sends the invoices, so it isn't invoiced.</Alert>
                                )}
                                {!preview.monthFinished && (
                                    <Alert status="warning"><AlertIcon />{preview.monthLabel} isn't over yet. This is month-to-date; you can send once the month ends.</Alert>
                                )}
                                {preview.stripeMode === 'missing' && (
                                    <Alert status="error"><AlertIcon />The billing laundry has no Stripe secret key saved.</Alert>
                                )}
                                {preview.existingInvoice && (
                                    <Alert status={preview.existingInvoice.status === 'failed' ? 'error' : 'success'}>
                                        <AlertIcon />
                                        <Text fontSize="sm">
                                            {preview.existingInvoice.status === 'failed'
                                                ? `Last attempt failed: ${preview.existingInvoice.error || 'unknown error'}. You can send again.`
                                                : `Already invoiced ${money(preview.existingInvoice.total)} to ${preview.existingInvoice.recipientEmail}`
                                                  + (preview.existingInvoice.sentAt ? ` on ${new Date(preview.existingInvoice.sentAt).toLocaleDateString()}` : '')
                                                  + '.'}
                                            {preview.existingInvoice.hostedInvoiceUrl && (
                                                <> <Link color="blue.600" href={preview.existingInvoice.hostedInvoiceUrl} isExternal>View in Stripe</Link></>
                                            )}
                                        </Text>
                                    </Alert>
                                )}

                                <Text fontSize="sm" color="gray.600">
                                    Revenue for {preview.monthLabel}: <b>{money(preview.revenue)}</b>
                                    {' '}(orders {money(preview.orderRevenue)} + subscriptions {money(preview.subscriptionRevenue)})
                                </Text>

                                <Table size="sm">
                                    <Thead>
                                        <Tr><Th>Item</Th><Th isNumeric>Amount</Th></Tr>
                                    </Thead>
                                    <Tbody>
                                        {preview.lines.map((l) => (
                                            <Tr key={l.key}>
                                                <Td>
                                                    <Text fontWeight="600" fontSize="sm">{l.description}</Text>
                                                    <Text fontSize="xs" color="gray.500">{l.detail}</Text>
                                                </Td>
                                                <Td isNumeric>{money(l.amount)}</Td>
                                            </Tr>
                                        ))}
                                        <Tr>
                                            <Td fontWeight="600">Subtotal</Td>
                                            <Td isNumeric>{money(preview.subtotal)}</Td>
                                        </Tr>
                                        <Tr>
                                            <Td>Texas sales tax ({preview.taxPercent}%)</Td>
                                            <Td isNumeric>{money(preview.tax)}</Td>
                                        </Tr>
                                        <Tr>
                                            <Td fontWeight="700" fontSize="md">Grand total</Td>
                                            <Td isNumeric fontWeight="700" fontSize="md">{money(preview.total)}</Td>
                                        </Tr>
                                    </Tbody>
                                </Table>
                                <Text fontSize="sm" color="gray.600">
                                    Due within {preview.dueDays} days of sending
                                    {preview.monthFinished && ` (by ${new Date(preview.dueDate + 'T00:00:00').toLocaleDateString()})`}.
                                    {preview.totalCents === 0 && ' Stripe records $0 invoices as paid, so the client gets our $0 invoice email instead of a Stripe email.'}
                                </Text>
                            </>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose}>Close</Button>
                    <Button colorScheme="green" onClick={send} isLoading={sending} loadingText="Sending" isDisabled={!canSend}>
                        Send invoice
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
